
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type EdgeChange,
  type NodeChange,
  type FinalConnectionState,
  type NodeTypes,
} from '@xyflow/react'
import { DeviceNode } from './DeviceNode'
import { CableEdge } from './CableEdge'
import { DEVICE_DRAG_TYPE, PalettePanel } from './PalettePanel'
import {
  ARROWS_KEY,
  CROSSINGS_KEY,
  DEFAULT_PANELS,
  EMPTY_STRINGS,
  PANELS_KEY,
  type PanelPrefs,
  RECENT_KEY,
  boolValue,
  crossingValue,
  panelsValue,
  pushRecent,
  stringList,
  useLocalStorage,
} from '@/lib/use-local-storage'
import { Inspector } from './Inspector'
import { PanelRail } from './PanelRail'
import { CableMarkers, arrowMarkerId } from './CableMarkers'
import { CanvasMenu, type MenuItem } from './CanvasMenu'
import { ShowSummary } from './ShowSummary'
import { SignalLegend } from './SignalLegend'
import { ExportMenu } from './ExportMenu'
import { ShareMenu } from './ShareMenu'
import { PackListPanel } from './PackListPanel'
import { SHOW_FORMATS } from '@/lib/formats'
import { carriesSdi } from '@/lib/capability'
import type { CableProduct, CableRun } from '@/lib/cables'
import { summariseShow } from '@/lib/show-summary'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { term } from '@/lib/ui-mode'
import { CanvasViewContext } from './CanvasView'
import { SIGNAL_META, type SignalCategory } from '@/lib/signals'
import { assignLanes, edgeGeometry } from '@/lib/cable-lanes'
import { cableRoute, findCrossings, type CrossingStyle, type Jump, type Point } from '@/lib/cable-route'
import { evaluateConnection as connectionRules, sharedOutputs } from '@/lib/connection-rules'
import { splitterPorts, type SplitterOption } from '@/lib/splitters'
import { Icon } from '@/components/ui/Icon'
import { btn, cx, iconBtn, inputSm, notice } from '@/lib/ui'
import Link from 'next/link'
import { UnsavedGate } from './UnsavedGate'
import {
  handleIdFor,
  nodeDisplayName,
  portIdFromHandle,
  type CanvasEdgeDTO,
  type CanvasNodeDTO,
  type DeviceDTO,
  type DevicePaletteDTO,
  type InventoryItemDTO,
  type PortDTO,
  type ProjectDetailDTO,
} from '@/lib/types'
import type { DeviceFlowNode } from './types'
import { errorMessage } from '@/lib/client-api'

const nodeTypes: NodeTypes = { device: DeviceNode }

/** One cable type, so parallel runs between the same devices get their own lanes. */
const edgeTypes = { cable: CableEdge }

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

/**
 * What copy carries between pastes: devices with their positions relative to
 * the group's corner, and the cables that ran between them. Module-level so
 * it survives moving to another project in the same tab; a device that does
 * not exist there is simply skipped on paste.
 */
type ClipNode = { key: string; deviceId: string; label: string | null; dx: number; dy: number }
type ClipEdge = {
  source: string
  target: string
  sourceHandle: string | null
  targetHandle: string | null
  data: Record<string, unknown>
}
let clipboard: { nodes: ClipNode[]; edges: ClipEdge[] } | null = null

const PASTE_OFFSET = 40

type CanvasMenuState = {
  kind: 'pane' | 'node' | 'edge' | 'selection' | 'split'
  /** Relative to the canvas panel, for the popover. */
  x: number
  y: number
  /** In flow coordinates, for placing things. */
  flow: { x: number; y: number }
  id?: string
  /** For 'split': the boxes that can do it, and a second input to feed too. */
  split?: { options: SplitterOption[]; alsoFeed?: CableEnd }
}

/** One end of a cable: a device and the handle it is plugged into. */
type CableEnd = { target: string; targetHandle: string }

/** What a move or a split just did, and the way back. */
type CableNotice = { message: string; undo?: () => void; split?: () => void }

/** How a notice names a cable: its tape number, or "the cable". */
const cableName = (edge: Pick<Edge, 'label'>) => (typeof edge.label === 'string' && edge.label.trim()) || 'the cable'

type SaveState =
  | { status: 'clean' }
  | { status: 'dirty' }
  | { status: 'saving' }
  | { status: 'saved'; at: string }
  | { status: 'error'; message: string }

const ALL_SIGNALS_VISIBLE: Record<SignalCategory, boolean> = {
  VIDEO: true,
  AUDIO: true,
  NETWORK: true,
  DATA: true,
  POWER: true,
}

/** Fallbacks for nodes React Flow has not measured yet. */
const NODE_W = 264
const NODE_H = 180
const GUTTER = 40
/**
 * Click-to-add drops the first node below the signal legend, not beside it,
 * so its header and left-hand handles are never under the panel. Only the y
 * anchor moves: the x positions are what the smoke suites expect.
 */
const LEGEND_CLEARANCE = 170

function findFreePosition(
  nodes: DeviceFlowNode[],
  start: { x: number; y: number },
): { x: number; y: number } {
  const boxes = nodes.map((node) => ({
    x: node.position.x,
    y: node.position.y,
    w: node.measured?.width ?? NODE_W,
    h: node.measured?.height ?? NODE_H,
  }))

  const overlaps = (x: number, y: number) =>
    boxes.some(
      (box) =>
        x < box.x + box.w + GUTTER &&
        x + NODE_W + GUTTER > box.x &&
        y < box.y + box.h + GUTTER &&
        y + NODE_H + GUTTER > box.y,
    )

  const step = NODE_W + GUTTER
  for (let row = 0; row < 12; row++) {
    for (let column = 0; column < 8; column++) {
      const x = start.x + column * step
      const y = start.y + row * (NODE_H + GUTTER)
      if (!overlaps(x, y)) return { x, y }
    }
  }
  return { x: start.x + nodes.length * step, y: start.y }
}

/**
 * The free spot closest to `anchor`, looking every way round it in widening
 * rings. A splitter belongs between the two devices it joins; the scan above
 * only walks right and down, so in a tight gap it landed off-screen past the
 * next device.
 */
function findNearestFreePosition(nodes: DeviceFlowNode[], anchor: { x: number; y: number }): { x: number; y: number } {
  const boxes = nodes.map((node) => ({
    x: node.position.x,
    y: node.position.y,
    w: node.measured?.width ?? NODE_W,
    h: node.measured?.height ?? NODE_H,
  }))
  const free = (x: number, y: number) =>
    !boxes.some(
      (box) => x < box.x + box.w + GUTTER && x + NODE_W + GUTTER > box.x && y < box.y + box.h + GUTTER && y + NODE_H + GUTTER > box.y,
    )
  const stepX = (NODE_W + GUTTER) / 2
  const stepY = (NODE_H + GUTTER) / 2
  for (let ring = 0; ring <= 12; ring++) {
    const spots: Array<{ x: number; y: number; d: number }> = []
    for (let i = -ring; i <= ring; i++) {
      for (let j = -ring; j <= ring; j++) {
        if (Math.max(Math.abs(i), Math.abs(j)) !== ring) continue
        spots.push({ x: anchor.x + i * stepX, y: anchor.y + j * stepY, d: Math.hypot(i * stepX, j * stepY) })
      }
    }
    spots.sort((a, b) => a.d - b.d)
    const spot = spots.find(({ x, y }) => free(x, y))
    if (spot) return { x: spot.x, y: spot.y }
  }
  return findFreePosition(nodes, anchor)
}

function buildNode(node: CanvasNodeDTO, device: DeviceDTO): DeviceFlowNode {
  return {
    id: node.id,
    type: 'device',
    position: { x: node.x, y: node.y },
    // React Flow reads this out when a node takes focus. Without it the canvas
    // announced a row of unnamed regions, which is no help at all when the
    // whole point is telling one box from the next.
    ariaLabel: `${nodeDisplayName(node.label, device.name)}, ${device.manufacturer} ${device.model}`,
    data: { device, label: node.label, inventoryItemId: node.inventoryItemId },
  }
}

/**
 * Colour AND a dash pattern. The signal hues sit close in luminance, so
 * colour on its own left the categories indistinguishable in a greyscale print
 * of the schematic and to anyone who cannot separate those hues.
 */
function edgeStyleFor(port: PortDTO | undefined) {
  // No strokeWidth here: globals.css sets 2, and 3.5 on a selected cable. An
  // inline width beat that rule, so a selected cable never looked selected.
  if (!port) return { stroke: 'var(--muted)' }
  const meta = SIGNAL_META[port.signal]
  return { stroke: meta.color, strokeDasharray: meta.dash }
}

/** Whether a cable gives way at exactly the same places as last frame. */
function sameJumps(a: readonly Jump[] | undefined, b: readonly Jump[] | undefined): boolean {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every((jump, i) => jump.x === b[i].x && jump.y === b[i].y && jump.on === b[i].on)
}

type CanvasProps = {
  project: ProjectDetailDTO
  devices: DevicePaletteDTO[]
  /** Full records for the devices already on this canvas; the rest load on demand. */
  placedDevices: DeviceDTO[]
  inventory: InventoryItemDTO[]
  workspaceName: string
  /** Keys the browser-local "Recently placed" list. */
  workspaceSlug: string
  /** Devices the workspace has starred; pinned at the top of the palette. */
  favouriteIds: string[]
  /** The cable catalogue a run may name a real product from. */
  cables: CableProduct[]
  /** "/w/<slug>" — page links. */
  basePath: string
  /** "/api/w/<slug>" — API calls. */
  apiBase: string
}

function CanvasInner({
  project,
  devices,
  placedDevices,
  inventory,
  workspaceName,
  workspaceSlug,
  favouriteIds,
  cables,
  basePath,
  apiBase,
}: CanvasProps) {
  const { screenToFlowPosition, getNodes, getEdges, getViewport, getInternalNode, fitView } =
    useReactFlow<DeviceFlowNode>()
  const wrapperRef = useRef<HTMLDivElement>(null)

  /**
   * Full records, keyed by device id. Only the devices already on the canvas
   * arrive with the page; the palette ships a slim row per device (no ports),
   * because the whole catalogue with every port is about a megabyte and the
   * detail only matters once something is actually placed. Anything dragged in
   * is fetched once and kept here.
   */
  const [loadedDevices, setLoadedDevices] = useState<DeviceDTO[]>(placedDevices)
  const deviceById = useMemo(
    () => new Map(loadedDevices.map((device) => [device.id, device])),
    [loadedDevices],
  )

  /**
   * The full record for a device, fetching it the first time it is needed.
   * Returns undefined only if the catalogue no longer has it.
   */
  const loadDevice = useCallback(
    async (deviceId: string): Promise<DeviceDTO | undefined> => {
      const known = deviceById.get(deviceId)
      if (known) return known
      const response = await fetch(`${apiBase}/devices/${deviceId}`)
      if (!response.ok) return undefined
      const device = (await response.json()) as DeviceDTO
      setLoadedDevices((current) =>
        current.some((candidate) => candidate.id === device.id) ? current : [...current, device],
      )
      return device
    },
    [deviceById, apiBase],
  )

  const portById = useMemo(() => {
    const index = new Map<string, PortDTO>()
    for (const device of loadedDevices) {
      for (const port of device.ports) index.set(port.id, port)
    }
    return index
  }, [loadedDevices])

  const initialNodes = useMemo(
    () =>
      project.nodes
        .map((node) => {
          const device = deviceById.get(node.deviceId)
          return device ? buildNode(node, device) : null
        })
        .filter((node): node is DeviceFlowNode => node !== null),
    [project.nodes, deviceById],
  )

  /**
   * Objective trigger: no SDI on the canvas, no reason to ask about formats.
   *
   * Matching "BNC" in the connector was not the same question. MADI BNC, BNC
   * (AES3id) and Composite (BNC) all contain it and none of them is SDI, so a
   * podcast rig with a MADI audio run was being asked to pick a show format.
   * `carriesSdi` is the same test the capability check uses.
   */
  const hasSdi = useMemo(
    () =>
      project.edges.some((edge) => {
        const port = portById.get(edge.sourcePortId)
        return port != null && carriesSdi(port)
      }),
    [project.edges, portById],
  )

  const initialEdges = useMemo(
    () =>
      project.edges.map<Edge>((edge) => {
        const sourcePort = portById.get(edge.sourcePortId)
        const targetPort = portById.get(edge.targetPortId)
        return {
          id: edge.id,
          source: edge.sourceNodeId,
          target: edge.targetNodeId,
          // Bi-directional ports carry a role suffix on their handle ids.
          sourceHandle: sourcePort ? handleIdFor(sourcePort, 'source') : edge.sourcePortId,
          targetHandle: targetPort ? handleIdFor(targetPort, 'target') : edge.targetPortId,
          label: edge.label ?? undefined,
          style: edgeStyleFor(sourcePort),
          data: {
            lengthM: edge.lengthM,
            cableGrade: edge.cableGrade,
            formatOverride: edge.formatOverride,
            cableId: edge.cableId,
          },
        }
      }),
    [project.edges, portById],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState<DeviceFlowNode>(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges)
  const [saveState, setSaveState] = useState<SaveState>({ status: 'clean' })
  const [packListOpen, setPackListOpen] = useState(false)
  const [menu, setMenu] = useState<CanvasMenuState | null>(null)
  const closeMenu = useCallback(() => setMenu(null), [])
  /** One format for the whole show; individual runs may override it. */
  const [showFormat, setShowFormat] = useState<string | null>(project.showFormat ?? null)
  const { advanced } = useUiMode()
  const [visibleSignals, setVisibleSignals] =
    useState<Record<SignalCategory, boolean>>(ALL_SIGNALS_VISIBLE)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const [connectionError, setConnectionError] = useState<string | null>(null)
  // What a move or a split just did, with Undo. One at a time: the next
  // action replaces it.
  const [cableNotice, setCableNotice] = useState<CableNotice | null>(null)
  /** The cable whose end is being dragged, while it is (see onReconnectStart). */
  const reconnecting = useRef<string | null>(null)
  /** Splitters per kind of cable, so a second Split opens at once. */
  const splitterCache = useRef(new Map<string, SplitterOption[]>())
  // The last few devices placed from this browser, so a crew building three
  // similar rigs is not scrolling to the same DeckLink every time.
  const [recentIds, setRecentIds] = useLocalStorage(RECENT_KEY(workspaceSlug), EMPTY_STRINGS, stringList)
  // Which side panels are open. Per browser, not per project: someone who
  // wants a wide canvas wants it on every show.
  const [panels, setPanels] = useLocalStorage(PANELS_KEY, DEFAULT_PANELS, panelsValue)
  // Arrowheads at the input end of every cable. On by default; a crew that
  // reads direction from the port columns can switch them off.
  const [showArrows, setShowArrows] = useLocalStorage(ARROWS_KEY, true, boolValue)
  const toggleArrows = useCallback(() => setShowArrows((current) => !current), [setShowArrows])
  // How a cable is drawn where it crosses another. Off by default, per
  // browser like the arrowheads: it is how someone likes to read cables, not
  // a fact about the show.
  const [crossings, setCrossings] = useLocalStorage<CrossingStyle>(CROSSINGS_KEY, 'off', crossingValue)
  const togglePanel = useCallback(
    (side: keyof PanelPrefs) => setPanels((current) => ({ ...current, [side]: !current[side] })),
    [setPanels],
  )
  // Stable handlers, so a memoised panel is not re-rendered by a new closure.
  const collapseLibrary = useCallback(() => togglePanel('library'), [togglePanel])
  const collapseDetails = useCallback(() => togglePanel('details'), [togglePanel])
  const favouriteSet = useMemo(() => new Set(favouriteIds), [favouriteIds])

  const toggleSignal = useCallback((signal: SignalCategory) => {
    setVisibleSignals((current) => ({ ...current, [signal]: !current[signal] }))
  }, [])
  const showAllSignals = useCallback(() => setVisibleSignals(ALL_SIGNALS_VISIBLE), [])

  const markDirty = useCallback(() => {
    setSaveState((current) => (current.status === 'saving' ? current : { status: 'dirty' }))
  }, [])

  const handleNodesChange = useCallback(
    (changes: NodeChange<DeviceFlowNode>[]) => {
      onNodesChange(changes)
      const structural = changes.some(
        (change) =>
          change.type === 'remove' ||
          change.type === 'add' ||
          change.type === 'replace' ||
          (change.type === 'position' && change.dragging === false),
      )
      if (structural) markDirty()
    },
    [onNodesChange, markDirty],
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      onEdgesChange(changes)
      if (changes.some((change) => change.type === 'remove' || change.type === 'add')) {
        markDirty()
      }
    },
    [onEdgesChange, markDirty],
  )

  /** Ports with a cable on them — kept visible even inside a collapsed run. */
  const patchedPortIds = useMemo(() => {
    const ids = new Set<string>()
    for (const edge of edges) {
      if (edge.sourceHandle) ids.add(portIdFromHandle(edge.sourceHandle))
      if (edge.targetHandle) ids.add(portIdFromHandle(edge.targetHandle))
    }
    return ids
  }, [edges])

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const viewState = useMemo(
    () => ({
      visibleSignals,
      patchedPortIds,
      expandedGroups,
      toggleGroup,
      collapseThreshold: 6,
    }),
    [visibleSignals, patchedPortIds, expandedGroups, toggleGroup],
  )

  const signalOf = useCallback(
    (handleId: string | null | undefined) =>
      handleId ? portById.get(portIdFromHandle(handleId))?.signal : undefined,
    [portById],
  )

  /**
   * Last render's output per cable, so an unchanged cable keeps its object
   * identity across frames. Keyed by edge id; `base` is the edge it was built
   * from, so a real edit still produces a new object.
   */
  const renderedCache = useRef(new Map<string, { base: Edge; out: Edge }>())

  /** Cables whose signal is filtered out are hidden rather than removed. */
  const renderedEdges = useMemo(() => {
    // Each cable's two ends, where React Flow draws them (getHandlePosition:
    // the handle's outer edge, at its middle). Positions come from `nodes`,
    // the frame about to be drawn, rather than the store, which lags it
    // mid-drag; handle bounds only change when a node is re-measured.
    const nodeById = new Map(nodes.map((node) => [node.id, node]))
    const cableEnd = (nodeId: string, handleId: string | null | undefined, kind: 'source' | 'target') => {
      const node = nodeById.get(nodeId)
      const bound = getInternalNode(nodeId)?.internals.handleBounds?.[kind]?.find((h) => h.id === handleId)
      if (!node || !bound) return undefined
      const x = bound.x + node.position.x
      return { x: kind === 'source' ? x + bound.width : x, y: bound.y + node.position.y + bound.height / 2 }
    }
    const ends = new Map<string, { s: Point; t: Point }>()
    for (const edge of edges) {
      const s = cableEnd(edge.source, edge.sourceHandle, 'source')
      const t = cableEnd(edge.target, edge.targetHandle, 'target')
      if (s && t) ends.set(edge.id, { s, t })
    }

    // Cables that would share a trunk are spread into lanes so four runs read
    // as four, not as one thick line — whether they join the same two devices
    // or merely converge on the same column — in the order that crosses them
    // least. Hidden cables keep their lane, so toggling a signal off does not
    // reshuffle the ones still on screen.
    const lanes = assignLanes(edges, (edge) => {
      const end = ends.get(edge.id)
      return end ? edgeGeometry(end.s.x, end.s.y, end.t.x, end.t.y) : undefined
    })

    // Where visible cables cross, from the routes CableEdge will draw. Hidden
    // signals drop out, so filtering one away takes its jumps with it. A
    // selected cable stays unbroken and whatever it crosses gives way to it.
    let jumpsById: Map<string, Jump[]> | undefined
    if (crossings !== 'off') {
      const routes: Array<{ id: string; points: Point[] }> = []
      const unbroken = new Set<string>()
      for (const edge of edges) {
        const signal = signalOf(edge.sourceHandle)
        const end = ends.get(edge.id)
        if ((signal && !visibleSignals[signal]) || !end) continue
        const offset = lanes.get(edge.id)?.offset ?? 0
        routes.push({ id: edge.id, points: cableRoute(end.s.x, end.s.y, end.t.x, end.t.y, offset).points })
        if (edge.selected) unbroken.add(edge.id)
      }
      jumpsById = findCrossings(routes, { unbroken })
    }

    // Identity is the point here. This memo reruns on every drag frame (a drag
    // moves the trunks, so the lanes have to be recomputed), and returning a
    // fresh object for each cable made React Flow re-render all of them, sixty
    // times a second — on a two-hundred-cable show, the jank people feel
    // first. Hand back the previous object whenever nothing about that cable
    // changed, and only the handful that actually moved re-render.
    /* eslint-disable react-hooks/refs -- The cache is read and written inside
       this memo only, and it can only ever return an object built from exactly
       the same inputs as the one it would otherwise build. A render that React
       throws away therefore costs nothing and can leave nothing stale: the
       ref holds an identity hint, never state the UI reads. */
    const previous = renderedCache.current
    const next = new Map<string, { base: Edge; out: Edge }>()
    const rendered = edges.map((edge) => {
      const signal = signalOf(edge.sourceHandle)
      const shown = signal ? visibleSignals[signal] : true
      const hidden = !shown
      // The bare marker id: React Flow wraps it in url('#…') itself, and a
      // url() handed in here came out as url('#url(#…)'), which points at
      // nothing, so no cable ever drew its arrowhead.
      const markerEnd = showArrows && signal ? arrowMarkerId(signal) : undefined
      const laneOffset = lanes.get(edge.id)?.offset ?? 0
      const jumps = jumpsById?.get(edge.id)

      const before = previous.get(edge.id)
      const was = before?.out.data as { laneOffset?: number; jumps?: Jump[]; crossings?: CrossingStyle } | undefined
      if (
        before &&
        before.base === edge &&
        before.out.hidden === hidden &&
        before.out.markerEnd === markerEnd &&
        was?.laneOffset === laneOffset &&
        sameJumps(was?.jumps, jumps) &&
        (!jumps || was?.crossings === crossings)
      ) {
        next.set(edge.id, before)
        return before.out
      }

      const out: Edge = {
        ...edge,
        hidden,
        markerEnd,
        // Only a cable that actually gives way carries the style, so changing
        // it re-renders those cables and leaves the rest alone.
        data: jumps ? { ...edge.data, laneOffset, jumps, crossings } : { ...edge.data, laneOffset },
      }
      next.set(edge.id, { base: edge, out })
      return out
    })
    renderedCache.current = next
    /* eslint-enable react-hooks/refs */
    return rendered
  }, [edges, nodes, getInternalNode, signalOf, visibleSignals, showArrows, crossings])

  /** Outputs still feeding two cables from before an output took one. */
  const outputsShared = useMemo(() => sharedOutputs(portById, edges), [portById, edges])

  const edgeCounts = useMemo(() => {
    const counts: Record<SignalCategory, number> = { VIDEO: 0, AUDIO: 0, NETWORK: 0, DATA: 0, POWER: 0 }
    for (const edge of edges) {
      const signal = signalOf(edge.sourceHandle)
      if (signal) counts[signal] += 1
    }
    return counts
  }, [edges, signalOf])

  /**
   * The show at a glance, from the live canvas rather than the saved project,
   * so the corner readout moves the moment a device lands or a length is
   * typed. Runs are shaped exactly as the pack-list API shapes them, so the
   * reach warnings here and in the "Cables" panel are the same warnings.
   */
  const showSummary = useMemo(() => {
    const nameOf = (id: string) => {
      const node = nodes.find((candidate) => candidate.id === id)
      return node?.data.label || node?.data.device.name || id
    }
    const runs: CableRun[] = []
    const runEnds: { source: string; target: string }[] = []
    for (const edge of edges) {
      const sourcePort = edge.sourceHandle ? portById.get(portIdFromHandle(edge.sourceHandle)) : undefined
      const targetPort = edge.targetHandle ? portById.get(portIdFromHandle(edge.targetHandle)) : undefined
      if (!sourcePort || !targetPort) continue
      const cableId = typeof edge.data?.cableId === 'string' ? edge.data.cableId : null
      runs.push({
        id: edge.id,
        signal: sourcePort.signal,
        sourceConnector: sourcePort.connector,
        targetConnector: targetPort.connector,
        lengthM: typeof edge.data?.lengthM === 'number' ? edge.data.lengthM : null,
        cableGrade: typeof edge.data?.cableGrade === 'string' ? edge.data.cableGrade : null,
        formatOverride: typeof edge.data?.formatOverride === 'string' ? edge.data.formatOverride : null,
        product: cableId ? (cables.find((cable) => cable.id === cableId) ?? null) : null,
        label: typeof edge.label === 'string' && edge.label ? edge.label : `${nameOf(edge.source)} → ${nameOf(edge.target)}`,
      })
      runEnds.push({ source: edge.source, target: edge.target })
    }
    return summariseShow(
      nodes.map((node) => ({ nodeId: node.id, device: node.data.device })),
      runs,
      runEnds,
      showFormat,
    )
  }, [nodes, edges, portById, cables, showFormat])

  /**
   * Phase 2 validation. Three rules, all physical:
   *   1. A cable carries one signal category — SDI cannot land on an XLR input.
   *   2. An input connector accepts exactly one cable.
   *   3. A bi-directional connector is either an input or an output, never both,
   *      so it takes one cable in total.
   *
   * Returns the reason on failure so the same logic can both block the drag and
   * explain it afterwards.
   */
  const evaluateConnection = useCallback(
    (
      source: string | null | undefined,
      sourceHandle: string | null | undefined,
      target: string | null | undefined,
      targetHandle: string | null | undefined,
    ) =>
      connectionRules(
        portById,
        getEdges(),
        { source, sourceHandle, target, targetHandle },
        { reconnecting: reconnecting.current ?? undefined },
      ),
    [portById, getEdges],
  )

  const isValidConnection = useCallback(
    (connection: Connection | Edge) =>
      evaluateConnection(
        connection.source,
        connection.sourceHandle,
        connection.target,
        connection.targetHandle,
      ).ok,
    [evaluateConnection],
  )

  /** "Switcher · HDMI In 1", for notices. */
  const endName = useCallback(
    (nodeId: string, handle: string | null | undefined) => {
      const node = getNodes().find((candidate) => candidate.id === nodeId)
      const port = handle ? portById.get(portIdFromHandle(handle)) : undefined
      const device = node ? nodeDisplayName(node.data.label, node.data.device.name) : 'a device'
      return port ? `${device} · ${port.name}` : device
    },
    [getNodes, portById],
  )

  /**
   * Plugs one cable in somewhere else, keeping everything else about it: its
   * id, its number on the tape, its length and its cable type, as unplugging
   * a real cable and plugging it in again would. Returns the cable as it was
   * and the way back, which refuses if the old port has been taken since.
   */
  const replug = useCallback(
    (edgeId: string, next: { source: string; sourceHandle: string; target: string; targetHandle: string }) => {
      const before = getEdges().find((edge) => edge.id === edgeId)
      if (!before) return null
      const sourcePort = portById.get(portIdFromHandle(next.sourceHandle))
      setEdges((current) =>
        current.map((edge) => (edge.id === edgeId ? { ...edge, ...next, style: edgeStyleFor(sourcePort) } : edge)),
      )
      markDirty()
      const undo = () => {
        const sourceMoved = before.source !== next.source || before.sourceHandle !== next.sourceHandle
        const taken = getEdges().some(
          (edge) =>
            edge.id !== edgeId &&
            ((edge.target === before.target && edge.targetHandle === before.targetHandle) ||
              (sourceMoved && edge.source === before.source && edge.sourceHandle === before.sourceHandle)),
        )
        setCableNotice(null)
        if (taken) {
          setConnectionError(`Cannot undo: ${endName(before.target, before.targetHandle)} has another cable now.`)
          return
        }
        setEdges((current) =>
          current.map((edge) =>
            edge.id === edgeId
              ? {
                  ...edge,
                  source: before.source,
                  sourceHandle: before.sourceHandle,
                  target: before.target,
                  targetHandle: before.targetHandle,
                  style: before.style,
                }
              : edge,
          ),
        )
        markDirty()
      }
      return { before, undo }
    },
    [getEdges, portById, setEdges, markDirty, endName],
  )

  /**
   * Opens the list of boxes that can split a cable: right-click → Split…, or
   * "Split to feed both" after a move, which feeds `alsoFeed` as well. The
   * list comes from the server (the palette carries no ports) and is kept per
   * kind of cable.
   */
  const openSplit = useCallback(
    async (edgeId: string, alsoFeed?: CableEnd, at?: Pick<CanvasMenuState, 'x' | 'y' | 'flow'>) => {
      const edge = getEdges().find((candidate) => candidate.id === edgeId)
      const from = edge?.sourceHandle ? portById.get(portIdFromHandle(edge.sourceHandle)) : undefined
      const to = edge?.targetHandle ? portById.get(portIdFromHandle(edge.targetHandle)) : undefined
      if (!edge || !from || !to) return
      const key = `${from.signal}|${from.connector}|${to.connector}`
      let options = splitterCache.current.get(key)
      if (!options) {
        try {
          const query = new URLSearchParams({ signal: from.signal, from: from.connector, to: to.connector })
          const response = await fetch(`${apiBase}/devices/splitters?${query}`)
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          options = (await response.json()) as SplitterOption[]
          splitterCache.current.set(key, options)
        } catch {
          setConnectionError('Could not load the splitters. Try again.')
          return
        }
      }
      // Without a right-click point, open under the notices at the top.
      const width = wrapperRef.current?.getBoundingClientRect().width ?? 640
      const place = at ?? { x: Math.max(8, width / 2 - 160), y: 64, flow: { x: 0, y: 0 } }
      setMenu({ kind: 'split', id: edgeId, ...place, split: { options, alsoFeed } })
    },
    [getEdges, portById, apiBase],
  )

  /**
   * Puts a splitter into a cable: the cable now runs into the splitter and
   * keeps its tape number, a new cable runs from the splitter's first output
   * to where it went, and with `alsoFeed` a second one runs to that input.
   */
  const splitCable = useCallback(
    async (edgeId: string, option: SplitterOption, alsoFeed?: CableEnd) => {
      const device = await loadDevice(option.device.id)
      const current = getEdges()
      const edge = current.find((candidate) => candidate.id === edgeId)
      const from = edge?.sourceHandle ? portById.get(portIdFromHandle(edge.sourceHandle)) : undefined
      const to = edge?.targetHandle ? portById.get(portIdFromHandle(edge.targetHandle)) : undefined
      if (!device || !edge || !edge.targetHandle || !from || !to) return
      // The second input only if nothing has been plugged into it since.
      const second =
        alsoFeed && !current.some((e) => e.target === alsoFeed.target && e.targetHandle === alsoFeed.targetHandle)
          ? alsoFeed
          : undefined
      const ports = splitterPorts(device, { from, to }, second ? 2 : 1)
      if (!ports) {
        setConnectionError(`${device.model} cannot split this cable.`)
        return
      }

      // Halfway between the two devices, or the nearest free spot to it.
      const placed = getNodes()
      const a = placed.find((node) => node.id === edge.source)
      const b = placed.find((node) => node.id === edge.target)
      const anchor =
        a && b
          ? {
              x: (a.position.x + (a.measured?.width ?? NODE_W) + b.position.x) / 2 - NODE_W / 2,
              y: (a.position.y + b.position.y) / 2,
            }
          : { x: 0, y: 0 }
      const position = findNearestFreePosition(placed, anchor)

      const nodeId = newId()
      const feeds: CableEnd[] = [{ target: edge.target, targetHandle: edge.targetHandle }, ...(second ? [second] : [])]
      const added: Edge[] = ports.outputs.flatMap((port, i) => {
        const feed = feeds[i]
        return feed
          ? [
              {
                id: newId(),
                source: nodeId,
                sourceHandle: handleIdFor(port, 'source'),
                target: feed.target,
                targetHandle: feed.targetHandle,
                type: 'cable',
                style: edgeStyleFor(port),
              },
            ]
          : []
      })
      const input = handleIdFor(ports.input, 'target')
      setNodes((nodesNow) => [
        ...nodesNow,
        buildNode({ id: nodeId, deviceId: device.id, label: null, x: position.x, y: position.y, inventoryItemId: null }, device),
      ])
      setEdges((edgesNow) => [
        ...edgesNow.map((e) => (e.id === edgeId ? { ...e, target: nodeId, targetHandle: input } : e)),
        ...added,
      ])
      setRecentIds((recent) => pushRecent(recent, device.id))
      markDirty()

      const undo = () => {
        // Takes the splitter out with every cable on it, and plugs the
        // original back where it was.
        setNodes((nodesNow) => nodesNow.filter((node) => node.id !== nodeId))
        setEdges((edgesNow) =>
          edgesNow
            .filter((e) => e.id === edgeId || (e.source !== nodeId && e.target !== nodeId))
            .map((e) => (e.id === edgeId ? { ...e, target: edge.target, targetHandle: edge.targetHandle } : e)),
        )
        markDirty()
        setCableNotice(null)
      }
      setCableNotice({
        message: second
          ? `Split ${cableName(edge)} with ${device.manufacturer} ${device.model}, feeding both inputs.`
          : `Split ${cableName(edge)} with ${device.manufacturer} ${device.model}. Drag from its free outputs to feed more inputs.`,
        undo,
      })
    },
    [loadDevice, getEdges, getNodes, portById, setNodes, setEdges, setRecentIds, markDirty],
  )

  /**
   * Plugs a cable in, from a drag or the Details panel. A second cable from an
   * output moves the one already there (an output feeds one cable), and the
   * notice offers the way back, or a splitter to feed both inputs.
   */
  const connectCable = useCallback(
    (source: string, sourceHandle: string, target: string, targetHandle: string) => {
      const verdict = evaluateConnection(source, sourceHandle, target, targetHandle)
      if (!verdict.ok) {
        if (verdict.reason) setConnectionError(verdict.reason)
        return
      }
      if (verdict.moves) {
        const moved = replug(verdict.moves, { source, sourceHandle, target, targetHandle })
        if (!moved) return
        const { before, undo } = moved
        const left = before.targetHandle ? { target: before.target, targetHandle: before.targetHandle } : undefined
        setCableNotice({
          message: `Moved ${cableName(before)} from ${endName(before.target, before.targetHandle)} to ${endName(target, targetHandle)}.`,
          undo,
          split: left ? () => void openSplit(before.id, left) : undefined,
        })
        return
      }
      const sourcePort = portById.get(portIdFromHandle(sourceHandle))
      setEdges((current) =>
        addEdge({ id: newId(), source, sourceHandle, target, targetHandle, style: edgeStyleFor(sourcePort) }, current),
      )
      markDirty()
    },
    [evaluateConnection, replug, endName, openSplit, portById, setEdges, markDirty],
  )

  /** The same connection a drag makes, from the Details panel. */
  const patchPorts = connectCable

  const onConnect = useCallback(
    (connection: Connection) => {
      const { source, sourceHandle, target, targetHandle } = connection
      if (source && sourceHandle && target && targetHandle) connectCable(source, sourceHandle, target, targetHandle)
    },
    [connectCable],
  )

  /**
   * Why a drag was refused, rather than letting the cable silently snap back.
   * Reading the final connection state is more reliable than remembering the
   * last rejection, which depends on how often React Flow revalidates.
   */
  const explainRefusal = useCallback(
    (state: FinalConnectionState) => {
      // Released over empty canvas rather than a handle — not an error.
      if (state.isValid || !state.fromHandle || !state.toHandle) return
      const from = state.fromHandle
      const to = state.toHandle
      const [source, sourceHandle, target, targetHandle] =
        from.type === 'source'
          ? [from.nodeId, from.id, to.nodeId, to.id]
          : [to.nodeId, to.id, from.nodeId, from.id]
      const result = evaluateConnection(source, sourceHandle, target, targetHandle)
      if (!result.ok && result.reason) setConnectionError(result.reason)
    },
    [evaluateConnection],
  )

  const onConnectEnd = useCallback(
    (_event: MouseEvent | TouchEvent, state: FinalConnectionState) => explainRefusal(state),
    [explainRefusal],
  )

  /**
   * Dragging a cable by one of its ends moves that end: the same cable,
   * plugged in somewhere else. While the drag lasts the cable does not count
   * as occupying its own ports (see `evaluateConnection`).
   */
  const onReconnectStart = useCallback((_event: React.MouseEvent, edge: Edge) => {
    reconnecting.current = edge.id
  }, [])

  const onReconnect = useCallback(
    (oldEdge: Edge, connection: Connection) => {
      const { source, sourceHandle, target, targetHandle } = connection
      if (!source || !sourceHandle || !target || !targetHandle) return
      if (!evaluateConnection(source, sourceHandle, target, targetHandle).ok) return
      const moved = replug(oldEdge.id, { source, sourceHandle, target, targetHandle })
      if (!moved) return
      const targetMoved = moved.before.target !== target || moved.before.targetHandle !== targetHandle
      setCableNotice({
        message: `Moved ${cableName(moved.before)} to ${targetMoved ? endName(target, targetHandle) : endName(source, sourceHandle)}.`,
        undo: moved.undo,
      })
    },
    [evaluateConnection, replug, endName],
  )

  const onReconnectEnd = useCallback(
    (_event: MouseEvent | TouchEvent, _edge: Edge, _handleType: unknown, state: FinalConnectionState) => {
      explainRefusal(state)
      reconnecting.current = null
    },
    [explainRefusal],
  )

  useEffect(() => {
    if (!cableNotice) return
    const timer = setTimeout(() => setCableNotice(null), 10000)
    return () => clearTimeout(timer)
  }, [cableNotice])

  useEffect(() => {
    if (!connectionError) return
    const timer = setTimeout(() => setConnectionError(null), 5000)
    return () => clearTimeout(timer)
  }, [connectionError])

  const addDevice = useCallback(
    (device: DeviceDTO, position: { x: number; y: number }) => {
      setNodes((current) => [
        ...current,
        buildNode(
          {
            id: newId(),
            deviceId: device.id,
            label: null,
            x: position.x,
            y: position.y,
            inventoryItemId: null,
          },
          device,
        ),
      ])
      setRecentIds((current) => pushRecent(current, device.id))
      markDirty()
    },
    [setNodes, markDirty, setRecentIds],
  )

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const deviceId = event.dataTransfer.getData(DEVICE_DRAG_TYPE)
      if (!deviceId) return
      // The drop position is read now: the event is recycled before the fetch
      // for a device the canvas has not seen yet resolves.
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      void loadDevice(deviceId).then((device) => {
        if (device) addDevice(device, position)
      })
    },
    [loadDevice, addDevice, screenToFlowPosition],
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  // `getNodes()` rather than the `nodes` array: closing over the array made
  // this callback new on every drag frame, and the 355-row palette re-rendered
  // and re-sorted with it. The store read is current either way.
  const addToCentre = useCallback(
    (device: DevicePaletteDTO) => {
      const bounds = wrapperRef.current?.getBoundingClientRect()
      const { x, y, zoom } = getViewport()
      const anchor = bounds
        ? screenToFlowPosition({ x: bounds.left + 80, y: bounds.top + 80 + LEGEND_CLEARANCE })
        : { x: -x / zoom + 80, y: -y / zoom + 80 + LEGEND_CLEARANCE }
      void loadDevice(device.id).then((full) => {
        if (full) addDevice(full, findFreePosition(getNodes(), anchor))
      })
    },
    [getViewport, screenToFlowPosition, addDevice, getNodes, loadDevice],
  )

  const setNodeLabel = useCallback(
    (nodeId: string, nextLabel: string) => {
      setNodes((current) =>
        current.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, label: nextLabel.trim() || null } }
            : node,
        ),
      )
      markDirty()
    },
    [setNodes, markDirty],
  )

  const setNodeUnit = useCallback(
    (nodeId: string, inventoryItemId: string | null) => {
      setNodes((current) =>
        current.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, inventoryItemId } } : node,
        ),
      )
      markDirty()
    },
    [setNodes, markDirty],
  )

  /** Units already assigned elsewhere on this canvas cannot be assigned twice. */
  const unitsInUse = useMemo(() => {
    const used = new Map<string, string>()
    for (const node of nodes) {
      if (node.data.inventoryItemId) used.set(node.data.inventoryItemId, node.id)
    }
    return used
  }, [nodes])

  const unitCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const item of inventory) counts.set(item.deviceId, (counts.get(item.deviceId) ?? 0) + 1)
    return counts
  }, [inventory])

  const setEdgeLabel = useCallback(
    (edgeId: string, nextLabel: string) => {
      setEdges((current) =>
        current.map((edge) =>
          edge.id === edgeId ? { ...edge, label: nextLabel.trim() || undefined } : edge,
        ),
      )
      markDirty()
    },
    [setEdges, markDirty],
  )

  /** Run length in metres, or null when cleared. Saved with the canvas. */
  const setEdgeLength = useCallback(
    (edgeId: string, lengthM: number | null) => {
      setEdges((current) =>
        current.map((edge) =>
          edge.id === edgeId ? { ...edge, data: { ...edge.data, lengthM } } : edge,
        ),
      )
      markDirty()
    },
    [setEdges, markDirty],
  )

  /**
   * Every cable touching one device, at once. Clicking through two hundred
   * edges is the reason lengths never get filled in, so the bulk path is the
   * one that has to be quick — a camera position is one distance, not twelve.
   */
  const setNodeCableLengths = useCallback(
    (nodeId: string, lengthM: number | null) => {
      setEdges((current) =>
        current.map((edge) =>
          edge.source === nodeId || edge.target === nodeId
            ? { ...edge, data: { ...edge.data, lengthM } }
            : edge,
        ),
      )
      markDirty()
    },
    [setEdges, markDirty],
  )

  /** Any one field on a cable's data — grade, format override. */
  const setEdgeField = useCallback(
    (edgeId: string, key: 'cableGrade' | 'formatOverride' | 'cableId', value: string | null) => {
      setEdges((current) =>
        current.map((edge) => (edge.id === edgeId ? { ...edge, data: { ...edge.data, [key]: value } } : edge)),
      )
      markDirty()
    },
    [setEdges, markDirty],
  )

  const removeEdge = useCallback(
    (edgeId: string) => {
      setEdges((current) => current.filter((edge) => edge.id !== edgeId))
      markDirty()
    },
    [setEdges, markDirty],
  )

  const removeNode = useCallback(
    (nodeId: string) => {
      setNodes((current) => current.filter((node) => node.id !== nodeId))
      setEdges((current) =>
        current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
      )
      markDirty()
    },
    [setNodes, setEdges, markDirty],
  )

  /** Makes these the only selected things on the canvas. */
  const selectOnly = useCallback(
    (nodeIds: readonly string[], edgeIds: readonly string[] = []) => {
      const nodeSet = new Set(nodeIds)
      const edgeSet = new Set(edgeIds)
      setNodes((current) => current.map((node) => ({ ...node, selected: nodeSet.has(node.id) })))
      setEdges((current) => current.map((edge) => ({ ...edge, selected: edgeSet.has(edge.id) })))
    },
    [setNodes, setEdges],
  )

  const selectedNodeIds = useMemo(() => nodes.filter((node) => node.selected).map((node) => node.id), [nodes])

  /** The selected devices and the cables running between them, as a clip. */
  const clipSelection = useCallback((): { nodes: ClipNode[]; edges: ClipEdge[] } | null => {
    const picked = nodes.filter((node) => node.selected)
    if (picked.length === 0) return null
    const minX = Math.min(...picked.map((node) => node.position.x))
    const minY = Math.min(...picked.map((node) => node.position.y))
    const ids = new Set(picked.map((node) => node.id))
    return {
      nodes: picked.map((node) => ({
        key: node.id,
        deviceId: node.data.device.id,
        label: node.data.label,
        dx: node.position.x - minX,
        dy: node.position.y - minY,
      })),
      edges: edges
        .filter((edge) => ids.has(edge.source) && ids.has(edge.target))
        .map((edge) => ({
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
          data: { ...(edge.data ?? {}) },
        })),
    }
  }, [nodes, edges])

  /**
   * Places a clip at a corner. New ids throughout; unit assignments are
   * dropped, since two placements can never claim the same physical box;
   * cable labels are dropped too, so a copied V-001 does not appear twice
   * on the run sheet. The pasted devices become the selection.
   */
  const placeClip = useCallback(
    (clip: { nodes: ClipNode[]; edges: ClipEdge[] }, corner: { x: number; y: number }) => {
      const idFor = new Map<string, string>()
      const added: DeviceFlowNode[] = []
      for (const item of clip.nodes) {
        const device = deviceById.get(item.deviceId)
        if (!device) continue
        const id = newId()
        idFor.set(item.key, id)
        added.push(
          buildNode(
            { id, deviceId: device.id, label: item.label, x: corner.x + item.dx, y: corner.y + item.dy, inventoryItemId: null },
            device,
          ),
        )
      }
      if (added.length === 0) return
      const addedEdges: Edge[] = clip.edges.flatMap((edge) => {
        const source = idFor.get(edge.source)
        const target = idFor.get(edge.target)
        if (!source || !target) return []
        const sourcePort = edge.sourceHandle ? portById.get(portIdFromHandle(edge.sourceHandle)) : undefined
        return [
          {
            id: newId(),
            source,
            target,
            sourceHandle: edge.sourceHandle ?? undefined,
            targetHandle: edge.targetHandle ?? undefined,
            type: 'cable',
            style: edgeStyleFor(sourcePort),
            data: { ...edge.data },
          },
        ]
      })
      setNodes((current) => [
        ...current.map((node) => ({ ...node, selected: false })),
        ...added.map((node) => ({ ...node, selected: true })),
      ])
      setEdges((current) => [...current.map((edge) => ({ ...edge, selected: false })), ...addedEdges])
      markDirty()
    },
    [deviceById, portById, setNodes, setEdges, markDirty],
  )

  const copySelection = useCallback(() => {
    const clip = clipSelection()
    if (clip) clipboard = clip
  }, [clipSelection])

  const pasteClipboard = useCallback(
    (at?: { x: number; y: number }) => {
      if (!clipboard) return
      // Without a pointer position, land beside the originals.
      const corner =
        at ??
        (() => {
          const picked = nodes.filter((node) => node.selected)
          const base = picked.length > 0 ? picked : nodes
          if (base.length === 0) return { x: PASTE_OFFSET, y: PASTE_OFFSET }
          return {
            x: Math.min(...base.map((node) => node.position.x)) + PASTE_OFFSET,
            y: Math.min(...base.map((node) => node.position.y)) + PASTE_OFFSET,
          }
        })()
      placeClip(clipboard, corner)
    },
    [nodes, placeClip],
  )

  const duplicateSelection = useCallback(() => {
    const clip = clipSelection()
    if (!clip) return
    const picked = nodes.filter((node) => node.selected)
    placeClip(clip, {
      x: Math.min(...picked.map((node) => node.position.x)) + PASTE_OFFSET,
      y: Math.min(...picked.map((node) => node.position.y)) + PASTE_OFFSET,
    })
  }, [clipSelection, nodes, placeClip])

  const deleteSelection = useCallback(() => {
    const gone = new Set(nodes.filter((node) => node.selected).map((node) => node.id))
    setNodes((current) => current.filter((node) => !gone.has(node.id)))
    setEdges((current) =>
      current.filter((edge) => !edge.selected && !gone.has(edge.source) && !gone.has(edge.target)),
    )
    markDirty()
  }, [nodes, setNodes, setEdges, markDirty])

  /** Opens the details panel on one thing and puts the cursor in a field. */
  const editInDetails = useCallback(
    (selection: { nodeId?: string; edgeId?: string }, fieldId: string) => {
      selectOnly(selection.nodeId ? [selection.nodeId] : [], selection.edgeId ? [selection.edgeId] : [])
      setPanels((current) => (current.details ? current : { ...current, details: true }))
      // After the panel has rendered the selection.
      setTimeout(() => {
        const field = document.getElementById(fieldId)
        if (field instanceof HTMLInputElement) {
          field.focus()
          field.select()
        } else if (field instanceof HTMLElement) {
          field.focus()
        }
      }, 60)
    },
    [selectOnly, setPanels],
  )

  /** Opens the library and puts the cursor in its search box. */
  const openLibrarySearch = useCallback(() => {
    setPanels((current) => (current.library ? current : { ...current, library: true }))
    setTimeout(() => document.querySelector<HTMLInputElement>('input[aria-label="Search equipment library"]')?.focus(), 60)
  }, [setPanels])

  const menuPosition = useCallback(
    (event: { clientX: number; clientY: number }) => {
      const bounds = wrapperRef.current?.getBoundingClientRect()
      return {
        x: event.clientX - (bounds?.left ?? 0),
        y: event.clientY - (bounds?.top ?? 0),
        flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
      }
    },
    [screenToFlowPosition],
  )

  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      event.preventDefault()
      setMenu({ kind: 'pane', ...menuPosition(event) })
    },
    [menuPosition],
  )

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault()
      // Right-clicking one of several selected devices acts on all of them;
      // right-clicking anything else makes it the selection.
      const inSelection = selectedNodeIds.includes(node.id)
      if (!inSelection) selectOnly([node.id])
      const many = inSelection && selectedNodeIds.length > 1
      setMenu({ kind: many ? 'selection' : 'node', id: node.id, ...menuPosition(event) })
    },
    [selectedNodeIds, selectOnly, menuPosition],
  )

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault()
      selectOnly([], [edge.id])
      setMenu({ kind: 'edge', id: edge.id, ...menuPosition(event) })
    },
    [selectOnly, menuPosition],
  )

  const onSelectionContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      setMenu({ kind: 'selection', ...menuPosition(event) })
    },
    [menuPosition],
  )

  const menuItems = useMemo<MenuItem[]>(() => {
    if (!menu) return []
    const mod = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+'
    const canPaste = clipboard !== null
    if (menu.kind === 'pane') {
      const recent = recentIds
        .map((id) => deviceById.get(id))
        .filter((device): device is DeviceDTO => device !== undefined)
        .slice(0, 4)
      return [
        { key: 'add', label: 'Add device…', icon: 'plus', onSelect: openLibrarySearch },
        ...(recent.length > 0 ? [{ kind: 'heading' as const, key: 'recent-h', label: 'Recently placed' }] : []),
        ...recent.map<MenuItem>((device) => ({
          key: `recent-${device.id}`,
          label: `${device.manufacturer} ${device.model}`,
          onSelect: () =>
            void loadDevice(device.id).then((full) => {
              if (full) addDevice(full, findFreePosition(nodes, menu.flow))
            }),
        })),
        { kind: 'separator', key: 's1' },
        { key: 'paste', label: 'Paste', icon: 'copy', hint: `${mod}V`, disabled: !canPaste, onSelect: () => pasteClipboard(menu.flow) },
        { key: 'fit', label: 'Fit to view', icon: 'collapse', onSelect: () => void fitView({ padding: 0.2, maxZoom: 1, duration: 300 }) },
        ...(expandedGroups.size > 0
          ? [{ key: 'collapse', label: 'Collapse port runs', icon: 'collapse', onSelect: () => setExpandedGroups(new Set()) } as MenuItem]
          : []),
      ]
    }
    if (menu.kind === 'node' && menu.id) {
      const id = menu.id
      return [
        { key: 'rename', label: 'Rename', icon: 'sheet', onSelect: () => editInDetails({ nodeId: id }, 'node-label') },
        // The field it jumps to is Advanced-only, so the item is too: it would
        // otherwise open Details and focus something that is not there.
        ...(advanced
          ? ([{ key: 'unit', label: 'Assign unit', icon: 'inventory', onSelect: () => editInDetails({ nodeId: id }, 'node-unit') }] as MenuItem[])
          : []),
        { kind: 'separator', key: 's1' },
        { key: 'copy', label: 'Copy', icon: 'copy', hint: `${mod}C`, onSelect: copySelection },
        { key: 'dup', label: 'Duplicate', icon: 'plus', hint: `${mod}D`, onSelect: duplicateSelection },
        { kind: 'separator', key: 's2' },
        { key: 'del', label: 'Delete', icon: 'trash', hint: 'Del', danger: true, onSelect: () => removeNode(id) },
      ]
    }
    if (menu.kind === 'edge' && menu.id) {
      const id = menu.id
      return [
        { key: 'length', label: 'Set length', icon: 'cable', onSelect: () => editInDetails({ edgeId: id }, 'edge-length') },
        { key: 'label', label: 'Label', icon: 'sheet', onSelect: () => editInDetails({ edgeId: id }, 'edge-label') },
        {
          key: 'split',
          label: 'Split…',
          icon: 'split',
          // After the menu has closed, which happens right after this runs.
          onSelect: () => queueMicrotask(() => void openSplit(id, undefined, { x: menu.x, y: menu.y, flow: menu.flow })),
        },
        { kind: 'separator', key: 's1' },
        { key: 'del', label: 'Delete', icon: 'trash', hint: 'Del', danger: true, onSelect: () => removeEdge(id) },
      ]
    }
    if (menu.kind === 'split' && menu.id && menu.split) {
      const edgeId = menu.id
      const { options, alsoFeed } = menu.split
      const edge = edges.find((candidate) => candidate.id === edgeId)
      // Boxes the workspace owns first; within that, the server's order.
      const owned = (option: SplitterOption) => unitCounts.get(option.device.id) ?? 0
      const ranked = [...options].sort((a, b) => Number(owned(b) > 0) - Number(owned(a) > 0)).slice(0, 10)
      return [
        { kind: 'heading', key: 'h', label: alsoFeed ? 'Feed both inputs with' : `Split ${edge ? cableName(edge) : 'the cable'} with` },
        ...(ranked.length === 0
          ? [{ key: 'none', label: 'No splitter for this cable in the library', disabled: true, onSelect: () => {} } as MenuItem]
          : ranked.map<MenuItem>((option) => ({
              key: option.device.id,
              label: option.device.model,
              title: `${option.device.manufacturer} ${option.device.model}: ${option.inputs} in, ${option.outputs} out${
                option.sameConnector ? '' : `, only ${option.matchingOutputs} with this cable's plug`
              }${owned(option) > 0 ? `, ${owned(option)} in your inventory` : ''}`,
              // Outputs that fit the destination, or a warning that they do not.
              hint: `${option.sameConnector ? `${option.inputs}→${option.matchingOutputs}` : 'other plugs'}${owned(option) > 0 ? ' · owned' : ''}`,
              onSelect: () => void splitCable(edgeId, option, alsoFeed),
            }))),
      ]
    }
    const count = selectedNodeIds.length
    return [
      { kind: 'heading', key: 'h', label: `${count} devices` },
      { key: 'copy', label: 'Copy', icon: 'copy', hint: `${mod}C`, onSelect: copySelection },
      { key: 'dup', label: 'Duplicate', icon: 'plus', hint: `${mod}D`, onSelect: duplicateSelection },
      { kind: 'separator', key: 's1' },
      { key: 'del', label: 'Delete', icon: 'trash', hint: 'Del', danger: true, onSelect: deleteSelection },
    ]
  }, [
    menu, recentIds, deviceById, nodes, edges, expandedGroups, selectedNodeIds, advanced, unitCounts,
    openLibrarySearch, addDevice, loadDevice, pasteClipboard, fitView, editInDetails,
    copySelection, duplicateSelection, deleteSelection, removeNode, removeEdge, openSplit, splitCable,
  ])

  const save = useCallback(async (): Promise<boolean> => {
    setSaveState({ status: 'saving' })
    const payload = {
      nodes: nodes.map<CanvasNodeDTO>((node) => ({
        id: node.id,
        deviceId: node.data.device.id,
        label: node.data.label,
        x: node.position.x,
        y: node.position.y,
        inventoryItemId: node.data.inventoryItemId,
      })),
      edges: edges
        .filter((edge) => edge.sourceHandle && edge.targetHandle)
        .map<CanvasEdgeDTO>((edge) => ({
          id: edge.id,
          sourceNodeId: edge.source,
          // Handle suffixes are a rendering concern; the database stores port ids.
          sourcePortId: portIdFromHandle(edge.sourceHandle as string),
          targetNodeId: edge.target,
          targetPortId: portIdFromHandle(edge.targetHandle as string),
          label: typeof edge.label === 'string' ? edge.label : null,
          lengthM: typeof edge.data?.lengthM === 'number' ? edge.data.lengthM : null,
          cableGrade: typeof edge.data?.cableGrade === 'string' ? edge.data.cableGrade : null,
          formatOverride:
            typeof edge.data?.formatOverride === 'string' ? edge.data.formatOverride : null,
          cableId: typeof edge.data?.cableId === 'string' ? edge.data.cableId : null,
        })),
      showFormat,
    }

    try {
      const response = await fetch(`${apiBase}/projects/${project.id}/canvas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error ?? 'Save failed')
      setSaveState({
        status: 'saved',
        at: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
      })
      return true
    } catch (cause) {
      setSaveState({
        status: 'error',
        message: errorMessage(cause, 'Save failed'),
      })
      return false
    }
  }, [nodes, edges, showFormat, project.id, apiBase])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void save()
        return
      }
      const target = event.target
      const typing = target instanceof HTMLElement && target.matches('input, textarea, select, [contenteditable="true"]')
      if (typing) return
      // Copy, paste and duplicate act on the canvas selection. With nothing
      // selected, copy and paste are left to the browser.
      if ((event.metaKey || event.ctrlKey) && !event.altKey) {
        const key = event.key.toLowerCase()
        if (key === 'c' && selectedNodeIds.length > 0) {
          event.preventDefault()
          copySelection()
        } else if (key === 'v' && clipboard) {
          event.preventDefault()
          pasteClipboard()
        } else if (key === 'd' && selectedNodeIds.length > 0) {
          event.preventDefault()
          duplicateSelection()
        }
        return
      }
      if (event.altKey) return
      // Bare brackets fold the side panels.
      if (event.key !== '[' && event.key !== ']') return
      event.preventDefault()
      togglePanel(event.key === '[' ? 'library' : 'details')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [save, togglePanel, selectedNodeIds, copySelection, pasteClipboard, duplicateSelection])

  useEffect(() => {
    if (saveState.status !== 'dirty') return
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [saveState.status])

  const selectedNode = nodes.find((node) => node.selected) ?? null
  const selectedEdge = selectedNode ? null : (edges.find((edge) => edge.selected) ?? null)
  const missingDevices = project.nodes.length - initialNodes.length

  // The only way back from an accidental delete used to be reloading without
  // saving, which nothing on screen said. This makes it an action.
  const discardChanges = useCallback(() => {
    setNodes(initialNodes)
    setEdges(initialEdges)
    setShowFormat(project.showFormat ?? null)
    setSaveState({ status: 'clean' })
  }, [initialNodes, initialEdges, project.showFormat, setNodes, setEdges])

  return (
    <CanvasViewContext.Provider value={viewState}>
      <UnsavedGate dirty={saveState.status === 'dirty'} save={save} />
      {/* A workbench: frosted palette and inspector floating either side of a
          white drawing surface. Pinned to the window height so a long
          equipment list can never push the canvas off-screen; on phones the
          bottom bar takes the last 6rem. */}
      <div className="flex h-[calc(100dvh-6rem)] min-h-0 gap-3 overflow-hidden p-3 sm:h-dvh sm:pl-0">
        {panels.library ? (
          <PalettePanel
            devices={devices}
            unitCounts={unitCounts}
            favouriteIds={favouriteSet}
            recentIds={recentIds}
            libraryPath={`${basePath}/library`}
            onAdd={addToCentre}
            onCollapse={collapseLibrary}
          />
        ) : (
          <PanelRail
            side="left"
            label="Equipment"
            shortcut="["
            className="hidden md:flex"
            onExpand={() => togglePanel('library')}
          />
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <div className="glass relative z-20 flex h-12 shrink-0 items-center gap-1.5 rounded-full px-1.5">
            <Link
              href={`${basePath}/projects`}
              className={cx(iconBtn, 'h-9 w-9 shrink-0')}
              title="Back to projects"
              aria-label="Back to projects"
            >
              <Icon name="back" className="h-4 w-4" />
            </Link>
            <h1 className="min-w-[4rem] max-w-[18rem] truncate text-sm font-semibold tracking-tight" title={project.name}>
              {project.name}
            </h1>
            <p className="hidden shrink-0 font-mono text-[11px] text-muted 2xl:block">
              {nodes.length} device{nodes.length === 1 ? '' : 's'} · {edges.length} cable
              {edges.length === 1 ? '' : 's'}
            </p>

            {/* One format for the whole show. It decides the data rate on every
                SDI run, which decides how far each one will actually reach.
                Hidden until there is an SDI cable to apply it to, and then
                only for people who have asked for the detail — but the reach
                warnings it drives still fire either way. */}
            {advanced && hasSdi && (
            <select
              aria-label="Show format"
              title="The format this show runs at — sets the data rate on every SDI run"
              className={cx(inputSm, 'hidden shrink-0 max-w-[11rem] md:block')}
              value={showFormat ?? ''}
              onChange={(event) => {
                setShowFormat(event.target.value || null)
                markDirty()
              }}
            >
              <option value="">Show format…</option>
              {SHOW_FORMATS.map((format) => (
                <option key={format.key} value={format.key}>
                  {format.label}
                </option>
              ))}
            </select>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {expandedGroups.size > 0 && (
                <button
                  type="button"
                  onClick={() => setExpandedGroups(new Set())}
                  title="Collapse every expanded port run"
                  className={cx(btn.sm, btn.ghost)}
                >
                  <Icon name="collapse" className="h-3.5 w-3.5" />
                  <span className="sr-only 2xl:not-sr-only">Collapse runs</span>
                </button>
              )}
              <SaveStatus state={saveState} />
              {saveState.status === 'dirty' && (
                <button
                  type="button"
                  onClick={discardChanges}
                  title="Put the canvas back to the last saved version"
                  className={cx(btn.sm, btn.ghost, 'max-md:hidden')}
                >
                  <span className="sr-only 2xl:not-sr-only">Discard</span>
                  <Icon name="back" className="h-3.5 w-3.5 2xl:hidden" />
                </button>
              )}
              <ShareMenu apiBase={apiBase} projectId={project.id} />
              <ExportMenu
                projectName={project.name}
                workspaceName={workspaceName}
                nodeCount={nodes.length}
                cableCount={edges.length}
              />
              <Link
                href={`${basePath}/projects/${project.id}/run-sheet`}
                className={cx(btn.sm, btn.secondary)}
                title={advanced ? 'Run sheet — patch list for the rigging crew' : 'Cable list — what plugs into what'}
              >
                <Icon name="sheet" className="h-3.5 w-3.5" />
                <span className="sr-only 2xl:not-sr-only">{term(advanced, 'runSheet')}</span>
              </Link>
              <button
                type="button"
                onClick={() => setPackListOpen(true)}
                className={cx(btn.sm, btn.secondary)}
                title="Cables to bring — the pack list for this show"
              >
                <Icon name="cable" className="h-3.5 w-3.5" />
                <span className="sr-only 2xl:not-sr-only">{term(advanced, 'packList')}</span>
              </button>
              <Link
                href={`${basePath}/power?from=${project.id}`}
                className={cx(btn.sm, btn.secondary)}
                title="Power — what this show draws, and whether the supply covers it"
              >
                <Icon name="plug" className="h-3.5 w-3.5" />
                <span className="sr-only 2xl:not-sr-only">Power</span>
              </Link>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saveState.status === 'saving'}
                className={cx(btn.sm, btn.primary)}
              >
                {saveState.status === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div
            ref={wrapperRef}
            className="panel relative min-h-0 flex-1 overflow-hidden rounded-[var(--radius-panel)]"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            {/* Notices sit top-centre, clear of the signal legend and the minimap in the corners. */}
            {(missingDevices > 0 || connectionError || cableNotice) && (
              <div className="pointer-events-none absolute left-1/2 top-3 z-10 flex max-w-md -translate-x-1/2 flex-col items-center gap-2">
                {missingDevices > 0 && (
                  <p className={cx(notice.warning, 'pointer-events-auto text-xs shadow-[var(--shadow-float)]')}>
                    {missingDevices} placed device{missingDevices === 1 ? '' : 's'} could not be loaded
                    because the equipment was removed from the library. Saving now will drop them from
                    this canvas.
                  </p>
                )}
                {connectionError && (
                  <p role="alert" className={cx(notice.error, 'pointer-events-auto text-xs shadow-[var(--shadow-float)]')}>
                    {connectionError}
                  </p>
                )}
                {cableNotice && (
                  <div
                    role="status"
                    className={cx(
                      notice.info,
                      'pointer-events-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs shadow-[var(--shadow-float)]',
                    )}
                  >
                    <span>{cableNotice.message}</span>
                    <span className="flex items-center gap-1.5">
                      {cableNotice.undo && (
                        <button type="button" onClick={cableNotice.undo} className={cx(btn.sm, btn.ghost)}>
                          Undo
                        </button>
                      )}
                      {cableNotice.split && (
                        <button
                          type="button"
                          onClick={() => {
                            const split = cableNotice.split
                            setCableNotice(null)
                            split?.()
                          }}
                          className={cx(btn.sm, btn.secondary)}
                        >
                          Split to feed both
                        </button>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            <ReactFlow
              nodes={nodes}
              edges={renderedEdges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              onConnectEnd={onConnectEnd}
              onReconnect={onReconnect}
              onReconnectStart={onReconnectStart}
              onReconnectEnd={onReconnectEnd}
              onPaneContextMenu={onPaneContextMenu}
              onNodeContextMenu={onNodeContextMenu}
              onEdgeContextMenu={onEdgeContextMenu}
              onSelectionContextMenu={onSelectionContextMenu}
              onPaneClick={closeMenu}
              onMoveStart={closeMenu}
              onNodeDragStart={closeMenu}
              isValidConnection={isValidConnection}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              // Only fit when there is something to fit. On an empty canvas
              // fitView fires as soon as the first node appears and moves the
              // viewport out from under the position we just computed from it,
              // landing that first device off-screen.
              fitView={initialNodes.length > 0}
              fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              minZoom={0.1}
              maxZoom={2}
              defaultEdgeOptions={{ type: 'cable' }}
              connectionLineStyle={{ stroke: 'var(--accent)', strokeWidth: 2 }}
              deleteKeyCode={['Backspace', 'Delete']}
              colorMode="light"
            >
              <Background variant={BackgroundVariant.Dots} gap={20} size={1.2} color="var(--canvas-dots)" />
              <CableMarkers />
              <SignalLegend
                visibleSignals={visibleSignals}
                counts={edgeCounts}
                onToggle={toggleSignal}
                onShowAll={showAllSignals}
                arrows={showArrows}
                onToggleArrows={toggleArrows}
                crossings={crossings}
                onCrossingsChange={setCrossings}
              />
              <Controls />
              <MiniMap
                position="top-right"
                pannable
                zoomable
                className="max-md:hidden"
                maskColor="var(--minimap-mask)"
                nodeColor="var(--minimap-node)"
                nodeStrokeColor="var(--minimap-stroke)"
              />
              <ShowSummary
                summary={showSummary}
                sharedOutputs={outputsShared}
                powerHref={`${basePath}/power?from=${project.id}`}
              />
            </ReactFlow>

            {/* Inside the canvas area so it covers the diagram, not the rail. */}
            {packListOpen && (
              <PackListPanel
                onClose={() => setPackListOpen(false)}
                apiBase={apiBase}
                projectId={project.id}
                projectName={project.name}
                showFormat={showFormat}
              />
            )}
            {menu && (
              <CanvasMenu x={menu.x} y={menu.y} items={menuItems} onClose={closeMenu} wide={menu.kind === 'split'} />
            )}
          </div>
        </div>

        {panels.details ? (
          <Inspector
            node={selectedNode}
            edge={selectedEdge}
            edges={edges}
            nodes={nodes}
            portById={portById}
            inventory={inventory}
            unitsInUse={unitsInUse}
            onLabelChange={setNodeLabel}
            onUnitChange={setNodeUnit}
            onEdgeLabelChange={setEdgeLabel}
            onEdgeLengthChange={setEdgeLength}
            onEdgeFieldChange={setEdgeField}
            showFormat={showFormat}
            cables={cables}
            onNodeCableLengths={setNodeCableLengths}
            onRemove={removeNode}
            onRemoveEdge={removeEdge}
            onPatch={patchPorts}
            validateConnection={evaluateConnection}
            libraryPath={`${basePath}/library`}
            onCollapse={collapseDetails}
          />
        ) : (
          <PanelRail
            side="right"
            label="Details"
            shortcut="]"
            attention={Boolean(selectedNode || selectedEdge)}
            onExpand={() => togglePanel('details')}
          />
        )}
      </div>
    </CanvasViewContext.Provider>
  )
}

function SaveStatus({ state }: { state: SaveState }) {
  const text =
    state.status === 'clean'
      ? 'No changes'
      : state.status === 'dirty'
        ? 'Unsaved changes'
        : state.status === 'saving'
          ? 'Saving…'
          : state.status === 'saved'
            ? `Saved ${state.at}`
            : state.message

  const tone =
    state.status === 'dirty'
      ? { text: 'text-warning', dot: 'bg-warning' }
      : state.status === 'error'
        ? { text: 'text-danger', dot: 'bg-danger' }
        : state.status === 'saved'
          ? { text: 'text-success', dot: 'bg-success' }
          : { text: 'text-muted', dot: 'bg-muted/40' }

  return (
    <span
      className={cx('flex shrink-0 items-center gap-1.5 px-1.5 font-mono text-[11px]', tone.text)}
      role="status"
      aria-live="polite"
      title={text}
    >
      <span aria-hidden="true" className={cx('h-1.5 w-1.5 rounded-full', tone.dot)} />
      {/* The dot alone below xl, the words from xl up — but an error always
          says what went wrong, at every width. The whole thing used to be
          `hidden … xl:flex`, so on a 13-inch laptop nothing on screen said
          whether the canvas was saved, and a failed save was silent. */}
      <span className={state.status === 'error' ? undefined : 'sr-only xl:not-sr-only'}>{text}</span>
    </span>
  )
}

export function CanvasWorkspace(props: CanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  )
}
